import React, { useState } from "react";
import { T } from "../theme";
import InvoicePill from "../components/InvoicePill";
import BilledStatusPill from "../finance/BilledStatus";
import InvoiceForm from "../invoice/InvoiceForm";
import { invoicesInHub } from "../invoice/prefill";
import validateConfig from "../finance/validateConfig";
import { formatMonth, addMonths, getCurrentMonth } from "../finance/calc";
import { fmtMoney } from "../finance/money";
import { toNative, requiredProblems } from "./configForm";
import { card, h3, hint, btn, Row, Text, Num, Select, RowControls, move, without, patchAt } from "./fields";

// Invoices: retainer and block invoices (part of Finance Config, saved with
// the shared draft), everything still to invoice (Finance Data, saved as you
// go), and the USD generator.
const clone = v => JSON.parse(JSON.stringify(v));
const STATUS = [["pending", "To invoice"], ["sent", "Invoiced"], ["paid", "Paid"]];

export default function InvoicesTab({ eng, form, setForm, dirty }) {
  const f = eng.engagement.fields;
  const [generating, setGenerating] = useState(null); // true = new, or a recorded invoice to regenerate
  const [saving, setSaving] = useState(false);
  const m = form.modules;
  const update = (fn) => setForm(prev => { const d = clone(prev || form); fn(d); return d; });
  const native = toNative(form);
  const problems = [...requiredProblems(form), ...validateConfig(native)];
  const save = async () => { if (problems.length) return; setSaving(true); const r = await eng.saveConfig(native, {}); setSaving(false); if (r === "saved") setForm(null); };

  const retInv = m.retainer?.invoices || [];
  const blkInv = m.sessionBlock?.invoices || [];
  const setRet = (list) => update(d => { d.modules.retainer.invoices = list; });
  const setBlk = (list) => update(d => { d.modules.sessionBlock.invoices = list; });

  // Still to invoice: billed-separately line items by month, plus extras
  const open = [];
  Object.entries(eng.finance).forEach(([month, md]) => (md.lineItems || []).forEach(li => { if (li.billedSeparately) open.push({ kind: "line", month, item: li }); }));
  eng.extras.forEach(x => open.push({ kind: "extra", item: x }));
  const outstanding = open.filter(o => (o.item.status || "to-invoice") === "to-invoice");

  // Record (or update) a generated invoice as a retainer invoice in the draft, matched by number.
  const recordInvoice = (entry, refs = []) => {
    // The work this invoice carries is now invoiced; saves straight away (Finance Data).
    refs.forEach(ref => {
      const li = /^li-(\d{4}-\d{2})-(.+)$/.exec(ref); const ex = /^ex-(.+)$/.exec(ref);
      if (li) eng.financeActions.updateLineItem(li[1], li[2], { status: "invoiced", invoice: entry.number });
      else if (ex) eng.financeActions.updateExtra(ex[1], { status: "invoiced", invoice: entry.number });
    });
    update(d => {
      if (!d.modules.retainer) return;
      const list = d.modules.retainer.invoices || [];
      const i = list.findIndex(x => x.number === entry.number);
      d.modules.retainer.invoices = i >= 0 ? list.map((x, k) => k === i ? { ...x, ...entry } : x) : [...list, entry];
    });
  };
  if (generating) return <InvoiceForm engagement={eng.engagement} record={generating === true ? undefined : generating} onRecord={m.retainer ? recordInvoice : undefined} onClose={() => setGenerating(null)} />;

  return (
    <div>
      <div style={{ position: "sticky", top: 0, zIndex: 2, background: T.bgSurface, padding: "6px 0 12px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: problems.length ? T.statusOff : T.fgMuted }}>{problems.length ? `${problems.length} thing${problems.length === 1 ? "" : "s"} to fix in Setup before saving` : dirty ? "Unsaved changes" : "Everything saved"}</div>
        <div style={{ display: "flex", gap: 8 }}>
          {invoicesInHub(f) && <button onClick={() => setGenerating(true)} style={btn(false)}>Generate invoice</button>}
          <button onClick={() => setForm(null)} disabled={!dirty} style={{ ...btn(false), opacity: dirty ? 1 : 0.5 }}>Discard</button>
          <button onClick={save} disabled={!dirty || saving || problems.length > 0} style={{ ...btn(true), opacity: dirty && !problems.length ? 1 : 0.5 }}>{saving ? "Saving…" : "Save invoices"}</button>
        </div>
      </div>

      {!invoicesInHub(f) && <p style={{ ...hint, marginBottom: 14 }}>This engagement is invoiced in Xero. Record the invoices here so the ledger and the client page match.</p>}
      {m.retainer && (
        <div style={card}>
          <h3 style={h3}>Retainer invoices</h3>
          <p style={hint}>One per month covered. Thread invoices a month ahead, so an invoice issued in June covers July. Lines are optional (the breakdown on the invoice).</p>
          {retInv.map((inv, i) => <RetainerInvoice key={i} inv={inv} onChange={p => setRet(patchAt(retInv, i, p))} onGenerate={invoicesInHub(f) ? () => setGenerating(inv) : undefined} controls={<RowControls onUp={() => setRet(move(retInv, i, -1))} onDown={() => setRet(move(retInv, i, 1))} onDelete={() => { if (window.confirm(`Remove ${inv.number || "this invoice"}?`)) setRet(without(retInv, i)); }} />} />)}
          <button type="button" onClick={() => setRet([...retInv, { number: "", date: eng.today, month: addMonths(getCurrentMonth(), 1), amount: m.retainer.monthlyTotal || 0, status: "sent" }])} style={btn(false)}>+ Add retainer invoice</button>
        </div>
      )}

      {m.sessionBlock && (
        <div style={card}>
          <h3 style={h3}>Block invoices · block {m.sessionBlock.blockNo || 1}</h3>
          <p style={hint}>Blocks are billed in tranches: how many sessions each invoice covers.</p>
          {blkInv.map((inv, i) => (
            <Row key={i} align="center">
              <Text value={inv.number} onChange={v => setBlk(patchAt(blkInv, i, { number: v }))} placeholder="INV-" />
              <Text value={inv.date} onChange={v => setBlk(patchAt(blkInv, i, { date: v }))} placeholder="YYYY-MM-DD" />
              <Num value={inv.sessions} onChange={v => setBlk(patchAt(blkInv, i, { sessions: v }))} placeholder="Sessions" />
              <Num value={inv.amount} onChange={v => setBlk(patchAt(blkInv, i, { amount: v }))} placeholder="Amount" />
              <Select value={inv.status || "sent"} onChange={v => setBlk(patchAt(blkInv, i, { status: v }))} options={[["sent", "Invoiced"], ["paid", "Paid"]]} />
              <RowControls onUp={() => setBlk(move(blkInv, i, -1))} onDown={() => setBlk(move(blkInv, i, 1))} onDelete={() => setBlk(without(blkInv, i))} />
            </Row>
          ))}
          <button type="button" onClick={() => setBlk([...blkInv, { number: "", date: eng.today, sessions: m.sessionBlock.purchased || 0, amount: 0, status: "sent" }])} style={btn(false)}>+ Add block invoice</button>
        </div>
      )}

      <div style={card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <h3 style={h3}>Work invoiced separately</h3>
          {outstanding.length > 0 && <span style={{ fontSize: 13, color: T.statusRisk, fontWeight: 600 }}>Still to invoice: {fmtMoney(outstanding.reduce((t, o) => t + (Number(o.item.amount) || 0), 0))}</span>}
        </div>
        <p style={hint}>Line items marked "invoice separately" and additional work. Click the status to advance it; this saves straight away.</p>
        {open.length === 0 && <p style={{ fontSize: 12, color: T.fgMuted, fontStyle: "italic" }}>Nothing invoiced separately.</p>}
        {open.sort((a, b) => (b.month || b.item.date || "").localeCompare(a.month || a.item.date || "")).map(o => (
          <div key={`${o.kind}-${o.item.id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "8px 0", borderTop: `1px solid ${T.borderSubtle}`, fontSize: 13 }}>
            <span><strong>{o.item.label}</strong> <span style={{ color: T.fgMuted, fontSize: 12 }}>· {o.month ? formatMonth(o.month) : o.item.date || "undated"}</span></span>
            <span style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <span style={{ fontWeight: 600 }}>{fmtMoney(o.item.amount)}</span>
              <BilledStatusPill item={o.item} onCycle={s => o.kind === "line" ? eng.financeActions.updateLineItem(o.month, o.item.id, { status: s }) : eng.financeActions.updateExtra(o.item.id, { status: s })} />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RetainerInvoice({ inv, onChange, controls, onGenerate }) {
  const [open, setOpen] = useState(false);
  const lines = inv.lines || [];
  return (
    <div style={{ borderTop: `1px solid ${T.borderSubtle}`, padding: "10px 0" }}>
      <Row align="center">
        <Text value={inv.number} onChange={v => onChange({ number: v })} placeholder="INV-" />
        <Text value={inv.date} onChange={v => onChange({ date: v })} placeholder="Issued YYYY-MM-DD" />
        <Text value={inv.month} onChange={v => onChange({ month: v })} placeholder="Covers YYYY-MM" />
        <Num value={inv.amount} onChange={v => onChange({ amount: v })} placeholder="Amount" />
        <Select value={inv.status || "sent"} onChange={v => onChange({ status: v })} options={STATUS} />
        <InvoicePill invoice={inv.status || "sent"} />
        <button type="button" onClick={() => setOpen(o => !o)} style={btn(false)}>{open ? "Hide lines" : `Lines (${lines.length})`}</button>
        {onGenerate && <button type="button" onClick={onGenerate} title="Open the invoice generator with this invoice's number, date and lines" style={btn(false)}>Generate</button>}
        {controls}
      </Row>
      {open && (
        <div style={{ paddingLeft: 12 }}>
          {lines.map((l, i) => (
            <Row key={i} align="center">
              <Text value={l.label} onChange={v => onChange({ lines: patchAt(lines, i, { label: v }) })} placeholder="Line" />
              <Num value={l.amount} onChange={v => onChange({ lines: patchAt(lines, i, { amount: v }) })} placeholder="Amount" />
              <RowControls onUp={() => onChange({ lines: move(lines, i, -1) })} onDown={() => onChange({ lines: move(lines, i, 1) })} onDelete={() => onChange({ lines: without(lines, i) })} />
            </Row>
          ))}
          <button type="button" onClick={() => onChange({ lines: [...lines, { label: "", amount: 0 }] })} style={btn(false)}>+ Add line</button>
          {lines.length > 0 && <span style={{ marginLeft: 12, fontSize: 12, color: T.fgMuted }}>Lines total {fmtMoney(lines.reduce((t, l) => t + (Number(l.amount) || 0), 0))}</span>}
        </div>
      )}
    </div>
  );
}
